import { LogHandler } from './log-handler';
import { Level } from './level';
import { LogRecord } from './log-record';
import { ConsoleHandler } from './console-handler';

export class Logger {
  public static GLOBAL_LOGGER_NAME = 'global';

  static readonly #instances = new Map<string, Logger>();

  readonly #name: string;
  #level: Level = Level.ERROR;
  #handlers: Set<LogHandler> = new Set();

  private constructor(name: string) {
    this.#name = name;
  }

  public static getLogger(name: string): Logger {
    if (name === Logger.GLOBAL_LOGGER_NAME) {
      throw new Error('Use `Logger.getGlobal()` instead.');
    }

    if (!Logger.#instances.has(name)) {
      const logger = new Logger(name);
      Logger.#instances.set(name, logger);
      return logger;
    }

    return Logger.#instances.get(name)!;
  }

  public static getGlobal(): Logger {
    if (!Logger.#instances.has(Logger.GLOBAL_LOGGER_NAME)) {
      const globalLogger = new Logger(Logger.GLOBAL_LOGGER_NAME);
      globalLogger.addHandler(new ConsoleHandler());
      Logger.#instances.set(Logger.GLOBAL_LOGGER_NAME, globalLogger);
    }

    return Logger.#instances.get(Logger.GLOBAL_LOGGER_NAME)!;
  }

  public getName = (): string => this.#name;

  public setLevel = (level: Level): void => {
    this.#level = level;
  };

  public getLevel = (): Level => this.#level;

  public addHandler = (handler: LogHandler): void => {
    if (handler.level === Level.OFF) {
      throw new Error('Cannot define log handler with level `Level.OFF`');
    }
    const hasPotentialDuplicate = Array.from(this.#handlers).some(
      (hdlr) => handler instanceof hdlr.constructor
    );
    if (hasPotentialDuplicate) {
      console.warn(
        `Added potentially duplicated log handler ${
          handler.constructor.name
        } to logger ${this.#name}`
      );
    }
    this.#handlers.add(handler);
  };

  public removeHandler = (handler: LogHandler): void => {
    this.#handlers.delete(handler);
  };

  public getHandlers = () => this.#handlers;

  public log = (level: Level, ...messages: any[]): void => {
    if (level < this.#level || this.#level === Level.OFF) {
      return;
    }
    const record: LogRecord = {
      loggerName: this.#name,
      level,
      messages,
      timestamp: Date.now(),
    };
    const applicableHandlers = this.getHandlersByLevel(level);
    applicableHandlers.forEach((hdlr) => hdlr.publish(record));
  };

  public debug = (...args: any[]): void => this.log(Level.DEBUG, ...args);
  public info = (...args: any[]): void => this.log(Level.INFO, ...args);
  public warn = (...args: any[]): void => this.log(Level.WARNING, ...args);
  public error = (...args: any[]): void => this.log(Level.ERROR, ...args);

  private getHandlersByLevel = (lookupLevel: Level): ReadonlySet<LogHandler> =>
    new Set(
      Array.from(this.#handlers).filter(
        ({ level }) => level === lookupLevel || level === Level.ALL
      )
    );
}
